import { JwtDelete, JwtSet } from './jwt.action';
import { JWT_STATE_CONFIG } from './jwt.injection-token';
import { CommonModule } from '@angular/common';
import { Inject, Injectable, NgModule, inject } from '@angular/core';
import { Action, NgxsModule, NgxsOnInit, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { JwtTokens, deserializeJwt } from '@wizbii-utils/angular/jwt';
import { CookieService } from 'ngx-cookie-service';

export type JwtStateModel = JwtTokens | null;
export const JwtStateToken = new StateToken<JwtStateModel>('jwt');

interface CookieConfig {
  name: string;
  value: string;
  expires: number | Date;
  path: string;
  cookieDomain: string;
}

@State({
  name: JwtStateToken,
  defaults: null,
})
@Injectable()
export class JwtState implements NgxsOnInit {
  @Selector([JwtStateToken])
  static state(state: JwtStateModel): JwtTokens | null {
    return state;
  }

  @Selector([JwtStateToken])
  static info(state: JwtStateModel): any {
    return state?.token ? deserializeJwt(state.token) : null;
  }

  @Selector([JwtStateToken])
  static isLogged(state: JwtStateModel): boolean {
    return !!state;
  }

  @Selector([JwtStateToken])
  static userId(state: JwtStateModel): string | null {
    const jwt = state?.token && deserializeJwt(state.token) ? deserializeJwt(state.token) : null;
    return state?.token && jwt ? jwt['user-id'] : null;
  }

  @Selector([JwtStateToken])
  static roles(state: JwtStateModel): string[] | null {
    const jwt = state?.token && deserializeJwt(state.token) ? deserializeJwt(state.token) : null;
    return state?.token && jwt ? jwt['roles'] : null;
  }

  readonly #jwtStateConfig = inject(JWT_STATE_CONFIG);
  readonly #cookieService = inject(CookieService);

  constructor() {
    if (
      !this.#jwtStateConfig.jwtCookieName ||
      !this.#jwtStateConfig.expiryCookieName ||
      !this.#jwtStateConfig.appEnvFqdn
    ) {
      throw new Error('[JwtState] Invalid configuration given');
    }
  }

  ngxsOnInit(ctx?: StateContext<any>): any {
    ctx?.setState(this.readTokens());
  }

  @Action(JwtSet)
  jwtSet(ctx: StateContext<JwtStateModel>, action: JwtSet): void {
    this.writeTokens(action.jwtTokens);
    ctx.setState(action.jwtTokens);
  }

  @Action(JwtDelete)
  jwtDelete(ctx: StateContext<JwtStateModel>): void {
    this.forgetTokens();
    ctx.setState(null);
  }

  private readTokens(): JwtTokens | null {
    const rawTokens = JSON.parse(this.#cookieService.get(this.#jwtStateConfig.jwtCookieName) || 'null');
    return rawTokens ? rawTokens : null;
  }

  private writeTokens(tokens: JwtTokens): void {
    const expiryExists = this.#cookieService.check(this.#jwtStateConfig.expiryCookieName);
    const msIn390Days = 1000 * 3600 * 24 * 390;
    const expiry = expiryExists
      ? new Date(parseInt(this.#cookieService.get(this.#jwtStateConfig.expiryCookieName), 10))
      : new Date(Date.now() + msIn390Days);

    if (!expiryExists) {
      this.setCookie({
        name: this.#jwtStateConfig.expiryCookieName,
        value: expiry.getTime().toString(),
        expires: expiry,
        path: '/',
        cookieDomain: this.#jwtStateConfig.appEnvFqdn,
      });
    }

    this.setCookie({
      name: this.#jwtStateConfig.jwtCookieName,
      value: JSON.stringify(tokens),
      expires: expiry,
      path: '/',
      cookieDomain: this.#jwtStateConfig.appEnvFqdn,
    });
  }

  private forgetTokens(): void {
    const forgetDate = new Date('Thu, 01 Jan 1970 00:00:01 GMT');

    this.setCookie({
      name: this.#jwtStateConfig.jwtCookieName,
      value: '',
      expires: forgetDate,
      path: '/',
      cookieDomain: this.#jwtStateConfig.appEnvFqdn,
    });

    this.setCookie({
      name: this.#jwtStateConfig.expiryCookieName,
      value: '',
      expires: forgetDate,
      path: '/',
      cookieDomain: this.#jwtStateConfig.appEnvFqdn,
    });
  }

  setCookie(cookieConfig: CookieConfig): void {
    this.#cookieService.set(
      cookieConfig.name,
      cookieConfig.value,
      cookieConfig.expires,
      cookieConfig.path,
      cookieConfig.cookieDomain === 'localhost' ? cookieConfig.cookieDomain : `.${cookieConfig.cookieDomain}`,
      cookieConfig.cookieDomain !== 'localhost',
      cookieConfig.cookieDomain === 'localhost' ? 'Lax' : 'None'
    );
  }
}

@NgModule({
  imports: [CommonModule, NgxsModule.forFeature([JwtState])],
})
export class JwtStateModule {}
